import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthContextType, User, LoginRequest, RegisterRequest } from '../types';
import { apiService } from '../services/apiService';

// Estado inicial
interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  error: null,
};

// Acciones del reducer
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS' }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_TOKEN'; payload: string }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        error: null,
      };

    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case 'REGISTER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        error: null,
      };

    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };

    case 'SET_TOKEN':
      return {
        ...state,
        token: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar el contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider del contexto
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restaurar usuario del localStorage al cargar
  useEffect(() => {
    const restoreAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken) {
        try {
          const user = JSON.parse(storedUser);
          apiService.setAuthToken(storedToken);
          dispatch({ type: 'SET_USER', payload: user });
          dispatch({ type: 'SET_TOKEN', payload: storedToken });
        } catch (error) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    restoreAuth();
  }, []);

  // Función de login
  const login = async (credentials: LoginRequest): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const tokenResponse = await apiService.auth.login(credentials);
      const token = tokenResponse.access_token;
      
      // Configurar token en el servicio API
      apiService.setAuthToken(token);
      
      // Obtener datos del usuario
      const user = await apiService.auth.getCurrentUser();
      
      // Guardar user Y token en localStorage
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { user, token } 
      });
    } catch (error: any) {
      let errorMessage = 'Error de login';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Si es un array de errores de validación
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((err: any) => err.msg).join(', ');
        }
        // Si es un string simple
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Si es un objeto con mensaje
        else if (data.message) {
          errorMessage = data.message;
        }
        // Si es un error de red o conexión
        else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Función de registro
  const register = async (userData: RegisterRequest): Promise<void> => {
    dispatch({ type: 'REGISTER_START' });
    try {
      await apiService.auth.register(userData);
      dispatch({ type: 'REGISTER_SUCCESS' });
    } catch (error: any) {
      let errorMessage = 'Error de registro';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Si es un array de errores de validación
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((err: any) => err.msg).join(', ');
        }
        // Si es un string simple
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Si es un objeto con mensaje
        else if (data.message) {
          errorMessage = data.message;
        }
        // Si es un error de red o conexión
        else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      dispatch({ type: 'REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Función de logout
  const logout = (): void => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    apiService.setAuthToken(null);
    dispatch({ type: 'LOGOUT' });
  };

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    isLoading: state.isLoading,
    error: state.error,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
